import React, { useState } from "react";
import { Button } from "../../../../ui/button";
import IconProvider from "../../../../ui/IconProvider";
import { FormField } from "../../types";
import MultiSelectDropdown, { MultiSelectOption } from "../../../../ui/dropdowns/MultiSelectDropdownWithButton";
import { useTypesDataContext } from "@/providers/TypesDataProvider";
import { useTagsDataContext } from "@/providers/TagsDataProvider";
import { useGlossaryBlueprintsDataContext } from "@/providers/GlossaryBlueprintsDataProvider";

interface FormFieldButtonsProps {
  field: FormField;
  sectionId: string;
  onUpdate: (
    sectionId: string,
    fieldId: string,
    updates: Partial<FormField>
  ) => void;
  onRemove: (sectionId: string, fieldId: string) => void;
  showDocEditor?: boolean;
  onToggleDocEditor?: () => void;
}

// Configuration for which buttons should be disabled for each field type
const FIELD_TYPE_BUTTON_CONFIG: Record<string, { multiple: boolean; filter: boolean; translation: boolean }> = {
  title: { multiple: false, filter: false, translation: true }, // Title fields don't support multiple or filter, but support translation
  textarea: { multiple: true, filter: false, translation: true }, // Textarea supports all
  number: { multiple: false, filter: false, translation: false }, // Number supports multiple and filter, but not translation
  file: { multiple: false, filter: false, translation: false }, // File supports multiple but not filter or translation
  url: { multiple: true, filter: false, translation: false }, // URL doesn't support any of these
  types: { multiple: true, filter: true, translation: false }, // Types supports filter and multi select (gets handled in TypesSelectionElement.tsx)
  glossaryItem: { multiple: true, filter: true, translation: false }, // Library Object supports filter by template
  document: { multiple: false, filter: true, translation: false }, // Document supports filter by tags, but not multiple or translation
};

export default function FormFieldButtons({
  field,
  sectionId,
  onUpdate,
  onRemove,
  showDocEditor = false,
  onToggleDocEditor,
}: FormFieldButtonsProps) {
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom'>('bottom');
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const { types, isLoading: typesLoading } = useTypesDataContext();
  const { tags, isLoading: tagsLoading } = useTagsDataContext();
  const { glossaryBlueprints, isLoading: blueprintsLoading } = useGlossaryBlueprintsDataContext();

  // Get button configuration for current field type
  const buttonConfig = FIELD_TYPE_BUTTON_CONFIG[field.type] || { multiple: true, filter: true, translation: true };

  // Determine dropdown position based on available screen space
  const determineDropdownPosition = (buttonElement: Element) => {
    if (typeof window !== 'undefined' && buttonElement) {
      const rect = buttonElement.getBoundingClientRect();
      setButtonRect(rect);
      
      // Calculate available space with dropdown height consideration
      const dropdownHeight = 300; // Fixed dropdown height
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Determine best position - prioritize below if there's enough space
      if (spaceBelow >= dropdownHeight) {
        // Enough space below, position below button
        setDropdownPosition('bottom');
      } else if (spaceAbove >= dropdownHeight) {
        // Enough space above, position above button
        setDropdownPosition('top');
      } else {
        // Limited space in both directions, choose the one with more space
        if (spaceBelow > spaceAbove) {
          setDropdownPosition('bottom');
        } else {
          setDropdownPosition('top');
        }
      }
    }
  };

  // Handle filter dropdown open
  const handleFilterDropdownOpen = (open: boolean, event?: React.MouseEvent) => {
    if (open) {
      // Get the button element from the event target or from data attribute
      let buttonElement: Element | null = null;
      if (event) {
        // Find the button element from the click event
        buttonElement = (event.target as Element).closest('[data-field-id]');
      }
      if (!buttonElement) {
        // Fallback to querySelector
        buttonElement = document.querySelector(`[data-field-id="${field.id}"]`);
      }
      
      if (buttonElement) {
        // Always recalculate position when opening to handle multiple buttons
        determineDropdownPosition(buttonElement);
      }
    } else {
      // Reset button position when closing to force fresh calculation next time
      setButtonRect(null);
    }
    setIsFilterDropdownOpen(open);
  };

  // Get filter options based on field type
  const getFilterOptions = (): MultiSelectOption[] => {
    if (field.type === 'types') {
      return types.map(type => ({
        id: type.id,
        label: type.name,
        checked: (field.filterOptions || []).includes(type.id)
      }));
    } else if (field.type === 'glossaryItem') {
      return glossaryBlueprints.map(blueprint => ({
        id: blueprint.id,
        label: blueprint.name,
        checked: (field.filterOptions || []).includes(blueprint.id)
      }));
    } else if (field.type === 'document') {
      // Document fields filter by tags
      return tags.map(tag => ({
        id: tag.id,
        label: tag.name,
        checked: (field.filterOptions || []).includes(tag.id)
      }));
    }
    return [];
  };

  // Handle filter options change
  const handleFilterOptionsChange = (selectedIds: string[]) => {
    // Automatically set filter to true when options are selected, false when none selected
    const filterActive = selectedIds.length > 0;
    onUpdate(sectionId, field.id, { 
      filterOptions: selectedIds,
      filter: filterActive
    });
  };

  // Get filter dropdown title and placeholder
  const getFilterConfig = () => {
    if (field.type === 'types') {
      return {
        title: "Select Types to Filter",
        placeholder: "Search types...",
        isLoading: typesLoading
      };
    } else if (field.type === 'glossaryItem') {
      return {
        title: "Select Templates to Filter",
        placeholder: "Search templates...",
        isLoading: blueprintsLoading
      };
    } else if (field.type === 'document') {
      return {
        title: "Select Tags to Filter Documents",
        placeholder: "Search tags...",
        isLoading: tagsLoading
      };
    }
    return {
      title: "Filter Options",
      placeholder: "Search...",
      isLoading: false
    };
  };

  const filterConfig = getFilterConfig();

  return (
    <div id="formfieldButtons" className="flex items-center gap-2">
      {/* Mandatory Toggle Button */}
      <Button
        variant="selection"
        size="sm"
        onClick={() =>
          onUpdate(sectionId, field.id, { mandatory: !field.mandatory })
        }
        data-state={field.mandatory ? "active" : "inactive"}
        className="px-2 text-xs w-8 h-8"
        tooltip="Makes this a mandatory field"
      >
        <IconProvider
          iconstring="fa-asterisk"
          size={14}
          color="currentColor"
          className="hover:opacity-80 transition-colors"
        />
      </Button>

      {/* List Toggle Button */}
      <Button
        variant="selection"
        size="sm"
        onClick={() =>
          onUpdate(sectionId, field.id, { multiple: !field.multiple })
        }
        data-state={field.multiple ? "active" : "inactive"}
        className={`px-2 text-xs w-8 h-8 ${!buttonConfig.multiple ? 'opacity-50 cursor-not-allowed' : ''}`}
        tooltip={buttonConfig.multiple ? "Allow users to add multiple values" : "Not available for this type"}
        disabled={!buttonConfig.multiple}
      >
        <IconProvider
          iconstring="fa-list"
          size={14}
          color="currentColor"
          className={`hover:opacity-80 transition-colors ${!buttonConfig.multiple ? 'opacity-50' : ''}`}
        />
      </Button>

      {/* Filter Dropdown - Always visible but only functional when enabled */}
      <div className="relative">
        {isFilterDropdownOpen && (
          <MultiSelectDropdown
            title={filterConfig.title}
            options={getFilterOptions()}
            selectedOptions={field.filterOptions || []}
            onSelectionChange={handleFilterOptionsChange}
            placeholder={filterConfig.placeholder}
            position={dropdownPosition}
            buttonRect={buttonRect}
            isOpen={isFilterDropdownOpen}
            onOpenChange={handleFilterDropdownOpen}
          />
        )}
        <Button
          variant="selection"
          size="sm"
          onClick={(e) => buttonConfig.filter ? handleFilterDropdownOpen(!isFilterDropdownOpen, e) : undefined}
          data-state={(field.filterOptions && field.filterOptions.length > 0) ? "active" : "inactive"}
          data-field-id={field.id}
          className={`px-2 text-xs w-8 h-8 relative ${!buttonConfig.filter ? 'opacity-50 cursor-not-allowed' : ''}`}
          tooltip={buttonConfig.filter ? "Select the elements the user can select from" : "Not available for this type"}
          disabled={!buttonConfig.filter}
        >
          <IconProvider
            iconstring="fa-filter"
            size={14}
            color="currentColor"
            className={`hover:opacity-80 transition-colors ${!buttonConfig.filter ? 'opacity-50' : ''}`}
          />
          {field.filterOptions && field.filterOptions.length > 0 && (
            <span className="absolute -top-1 -right-1 text-xs bg-blue-500/20 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded-full min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-medium">
              {field.filterOptions.length}
            </span>
          )}
        </Button>
      </div>

      {/* Translation Globe Icon */}
      <Button
        variant="selection"
        size="sm"
        onClick={() =>
          onUpdate(sectionId, field.id, { translation: !field.translation })
        }
        data-state={field.translation ? "active" : "inactive"}
        className={`px-2 text-xs w-8 h-8 ${!buttonConfig.translation ? 'opacity-50 cursor-not-allowed' : ''}`}
        tooltip={buttonConfig.translation ? "Automatically add this field to the translation" : "Not available for this type"}
        disabled={!buttonConfig.translation}
      >
        <IconProvider
          iconstring="fa-globe"
          size={14}
          color="currentColor"
          className={`hover:opacity-80 transition-colors ${!buttonConfig.translation ? 'opacity-50' : ''}`}
        />
      </Button>

      {/* Documentation Button */}
      {onToggleDocEditor && (
        <Button
          variant="selection"
          size="sm"
          onClick={onToggleDocEditor}
          data-state={field.documentation ? "active" : "inactive"}
          className="px-2 text-xs w-8 h-8"
          tooltip={showDocEditor ? "Hide documentation" : (field.documentation ? "Edit documentation" : "Add documentation")}
        >
          <IconProvider
            iconstring={showDocEditor ? "fa-chevron-up" : "fa-book"}
            size={14}
            color="currentColor"
            className="hover:opacity-80 transition-colors"
          />
        </Button>
      )}

      {/* Remove Field Button */}
      <Button
        variant="halfghost"
        size="sm"
        onClick={() => onRemove(sectionId, field.id)}
        className="px-2 text-xs w-8 h-8"
        tooltip="Remove this field"
      >
        <IconProvider
          iconstring="fa-xmark"
          size={14}
          color="currentColor"
          className="hover:opacity-80 transition-colors"
        />
      </Button>
    </div>
  );
}
